import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import config from "../../config";
import logger from "../../utils/logger";

/**
 * Dev-only "preview as provider" tool for the standalone (IAP) home page.
 *
 * Lets a developer type a provider's email to preview that provider's home
 * page. On submit it writes `user_email` into the URL; apiUtils forwards that
 * param to every backend call, and the backend dev bypass
 * (deps.py::_build_dev_iap_identity) synthesizes the identity from it. The
 * displayed user and the schedule then reflect the chosen provider.
 *
 * Renders nothing outside iap dev mode, so it can never appear in production or
 * in the pkce/iframe build.
 */
const ProviderPreviewBox: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeEmail = searchParams.get("user_email");
  const [value, setValue] = useState(activeEmail ?? "");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const isDevPreviewEnabled =
    config.AUTH_MODE === "iap" && config.ENVIRONMENT !== "production";
  if (!isDevPreviewEnabled) {
    return null;
  }

  const updateUserEmail = (email: string | null) => {
    const next = new URLSearchParams(searchParams); // preserve other params
    if (email) {
      next.set("user_email", email);
    } else {
      next.delete("user_email");
    }
    setSearchParams(next);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (trimmed && !trimmed.toLowerCase().endsWith("@blackbirdhealth.com")) {
      setErrorMessage("Email must end with @blackbirdhealth.com");
      return;
    }
    setErrorMessage(null);
    logger.devOnly.debug("ProviderPreviewBox: previewing as provider", {
      user_email: trimmed || null,
    });
    updateUserEmail(trimmed || null);
  };

  const handleClear = () => {
    setValue("");
    setErrorMessage(null);
    logger.devOnly.debug("ProviderPreviewBox: cleared provider preview");
    updateUserEmail(null);
  };

  return (
    <div className="bg-amber-50 border-b border-amber-300 px-6 py-2">
      <form
        onSubmit={handleSubmit}
        className="max-w-7xl mx-auto flex flex-wrap items-center gap-3"
      >
        <span className="text-xs font-bold uppercase tracking-wide text-amber-700">
          Dev
        </span>
        <label
          htmlFor="provider-preview-email"
          className="text-sm font-medium text-amber-900"
        >
          Preview as provider:
        </label>
        <input
          id="provider-preview-email"
          type="email"
          className="flex-1 min-w-[16rem] px-3 py-1 text-sm border border-amber-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
          placeholder="provider@blackbirdhealth.com"
          value={value}
          onChange={(e) => setValue(e.target.value)}
        />
        <button
          type="submit"
          className="px-4 py-1 text-sm font-medium text-white bg-amber-600 rounded-md hover:bg-amber-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-amber-500"
        >
          Preview
        </button>
        {activeEmail && (
          <button
            type="button"
            onClick={handleClear}
            className="px-3 py-1 text-sm font-medium text-amber-800 bg-amber-100 rounded-md hover:bg-amber-200 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-amber-500"
          >
            Reset
          </button>
        )}
        <span className="text-xs text-amber-700">
          Active:{" "}
          <span className="font-mono">
            {activeEmail ?? "dev.user@blackbirdhealth.com (default)"}
          </span>
        </span>
      </form>
      {errorMessage && (
        <div className="max-w-7xl mx-auto mt-1 text-xs text-red-600 font-medium">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export default ProviderPreviewBox;
